/**
 * Guía del portal: botón flotante + panel de preguntas, para cualquier portal.
 *
 * Solo consulta (dónde está cada cosa, cómo se hace y los informes que el agente sabe sacar);
 * nunca cambia datos. Se monta una vez en `AdminLayout`, que envuelve todas las rutas `/admin/*`
 * (ya protegidas por `PermissionRoute`). Qué agente toca lo decide `ia_agentes_disponibles()`
 * (filtrada por rol) según la ruta: sin agente para la pantalla, no se pinta nada. Un portal
 * nuevo solo necesita su fila en BD. La función vuelve a validar el permiso: si responde
 * `sin_permiso`, el botón se oculta el resto de la sesión para ese agente.
 *
 * Usa el chat global (`ChatIaPanel`, `ChatIaHistorial`, `ChatIaMensajes`, ...), el mismo del
 * Analista de tickets y del Agente CEO. Lo propio de la guía: respuesta en markdown con enlaces
 * internos que navegan, botones de enlace bajo la respuesta y el límite de preguntas por hora.
 * Repetir abre conversación nueva: el informe del mes se pide igual cada mes y debe salir con
 * los datos de hoy, sin arrastrar la charla anterior (que se queda en el historial).
 */
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { BotonFlotanteIA } from "./BotonFlotanteIA";
import { colorearSentido } from "./colorearSentido";
import { ChatIaCuerpo, ChatIaPanel } from "./ChatIaPanel";
import { ChatIaHistorial } from "./ChatIaHistorial";
import { ChatIaAviso, ChatIaMensajes, EsqueletoHilo } from "./ChatIaMensajes";
import { ChatIaComposer } from "./ChatIaComposer";
import { ChatIaBienvenida } from "./ChatIaBienvenida";
import { useAgenteGuia, useGuiaPortal } from "@/hooks/useGuiaPortal";
import { useHistorialChatIa } from "@/hooks/useHistorialChatIa";
import { useChatIaPanel } from "@/hooks/useChatIaPanel";
import {
  MAX_MENSAJE,
  SUGERENCIAS_RESPALDO,
  esRutaInterna,
  minutosRestantes,
  mostrarRestantes,
  textoRestantes,
  type AgenteDisponible,
  type GuiaEnlace,
  type MensajeGuia,
} from "@/lib/guiaPortal";
import { primeraPregunta, type ConversacionIa } from "@/lib/chatIa";

const DESCRIPCION_RESPALDO = "Te digo dónde está cada cosa y cómo se hace. Solo consulta, no cambia datos.";

export function GuiaPortal() {
  const location = useLocation();
  const agente = useAgenteGuia(location.pathname);
  if (!agente) return null;
  // `key`: al pasar de un portal a otro, la conversación del agente anterior no se mezcla.
  return <GuiaPortalAgente key={agente.codigo} agente={agente} />;
}

function GuiaPortalAgente({ agente }: { agente: AgenteDisponible }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const panel = useChatIaPanel();
  const [abriendo, setAbriendo] = useState<string | null>(null);
  const [borrador, setBorrador] = useState("");
  const finRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const guia = useGuiaPortal(agente.codigo, { sugerenciasHabilitadas: panel.abierto });
  const historial = useHistorialChatIa(agente.codigo, { habilitado: panel.abierto });
  const vacio = guia.mensajes.length === 0;

  // Baja al último mensaje al llegar uno nuevo o al empezar a escribir la respuesta.
  useEffect(() => {
    if (!panel.abierto) return;
    finRef.current?.scrollIntoView({ block: "end" });
  }, [panel.abierto, guia.mensajes.length, guia.enviando, guia.error, guia.idConversacion]);

  if (guia.oculta) return null;

  const inputDeshabilitado = guia.enviando || guia.bloqueado || guia.error?.codigo === "deshabilitado";
  const activa = historial.conversaciones.find((c) => c.numero !== null && c.numero === guia.idConversacion)?.id ?? null;

  const irA = (ruta: string) => {
    if (!esRutaInterna(ruta)) return;
    navigate(ruta);
    panel.cerrarEnPantallaChica();
  };

  const enfocarInput = () => window.setTimeout(() => inputRef.current?.focus(), 0);

  const enviar = async (texto: string, opciones?: { nueva?: boolean }) => {
    if (texto === borrador) setBorrador("");
    const devuelto = await guia.enviar(texto, location.pathname, opciones);
    if (devuelto !== null) setBorrador(devuelto);
  };

  const repetir = (texto: string) => {
    panel.volverAlChat();
    void enviar(texto, { nueva: true });
  };

  const nuevaConversacion = () => {
    guia.nuevaConversacion();
    setBorrador("");
    panel.volverAlChat();
    enfocarInput();
  };

  const cargar = async (c: ConversacionIa) => {
    setAbriendo(c.id);
    try {
      return await historial.cargarMensajes(c.id);
    } catch {
      toast({ title: "No se pudo abrir la conversación", description: "Intenta de nuevo en un momento.", variant: "destructive" });
      return null;
    } finally {
      setAbriendo(null);
    }
  };

  const abrirConversacion = async (c: ConversacionIa) => {
    if (c.numero === null) return;
    if (c.numero === guia.idConversacion) {
      panel.volverAlChat();
      return;
    }
    const mensajes = await cargar(c);
    if (!mensajes) return;
    const hilo: MensajeGuia[] = mensajes.map((m) => ({ id: m.id, rol: m.rol, texto: m.texto, enlaces: [] }));
    guia.abrirConversacion(c.numero, hilo);
    setBorrador("");
    panel.volverAlChat();
  };

  const repetirConversacion = async (c: ConversacionIa) => {
    const mensajes = await cargar(c);
    if (!mensajes) return;
    const pregunta = primeraPregunta(mensajes);
    if (!pregunta) {
      toast({ title: "Esa conversación no tiene preguntas para repetir" });
      return;
    }
    repetir(pregunta);
  };

  const eliminarConversacion = (c: ConversacionIa) => {
    historial.actualizar.mutate(
      { id: c.id, eliminar: true },
      { onError: () => toast({ title: "No se pudo eliminar la conversación", variant: "destructive" }) },
    );
    if (c.numero === guia.idConversacion) guia.nuevaConversacion();
  };

  const editar = (texto: string) => {
    setBorrador(texto.slice(0, MAX_MENSAJE));
    enfocarInput();
  };

  const sugerencias = guia.sugerencias ?? (guia.cargandoSugerencias ? null : [...SUGERENCIAS_RESPALDO]);

  const markdown: Components = {
    a: ({ href, children }) =>
      href && esRutaInterna(href) ? (
        <button
          type="button"
          onClick={() => irA(href)}
          className="font-medium text-primary underline underline-offset-2 hover:no-underline"
        >
          {children}
        </button>
      ) : (
        <span>{children}</span>
      ),
    // Los informes traen tablas: con borde, números alineados y scroll propio si no caben.
    table: ({ children }) => (
      <div className="not-prose my-2 max-w-full overflow-x-auto rounded-lg border border-border bg-background">
        <table className="w-full border-collapse text-[12.5px] tabular-nums">{children}</table>
      </div>
    ),
    thead: ({ children }) => <thead className="bg-muted/60">{children}</thead>,
    th: ({ children }) => (
      <th className="whitespace-nowrap border-b border-border px-3 py-2 text-left text-[11.5px] font-semibold text-muted-foreground">
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td className="whitespace-nowrap border-b border-border/60 px-3 py-1.5 text-foreground">{colorearSentido(children)}</td>
    ),
    // ▲ ingreso en verde y ▼ adeudo en rojo (ver colorearSentido).
    p: ({ children }) => <p>{colorearSentido(children)}</p>,
    li: ({ children }) => <li>{colorearSentido(children)}</li>,
    strong: ({ children }) => <strong>{colorearSentido(children)}</strong>,
  };

  const descripcion = agente.descripcion || DESCRIPCION_RESPALDO;

  return (
    <>
      <BotonFlotanteIA
        icono={Sparkles}
        texto={agente.nombre}
        tooltip={agente.descripcion || "Te digo dónde está cada cosa y cómo se hace"}
        ariaLabel={`Abrir ${agente.nombre}`}
        onClick={() => panel.setAbierto(true)}
        abierto={panel.abierto}
        orden={0}
      />

      <ChatIaPanel
        abierto={panel.abierto}
        onAbiertoChange={panel.setAbierto}
        titulo={agente.nombre}
        descripcion={descripcion}
        historialAbierto={panel.historialAbierto}
        onAlternarHistorial={panel.alternarHistorial}
        onNueva={nuevaConversacion}
        nuevaDeshabilitada={vacio && !guia.enviando}
        historial={
          <ChatIaHistorial
            conversaciones={historial.conversaciones}
            cargando={historial.cargando}
            error={historial.error}
            activa={activa}
            abriendo={abriendo}
            puedePreguntar={!inputDeshabilitado}
            onAbrir={(c) => void abrirConversacion(c)}
            onRepetir={(c) => void repetirConversacion(c)}
            onRenombrar={(c, titulo) => historial.actualizar.mutate({ id: c.id, titulo })}
            onFijar={(c, fijada) => historial.actualizar.mutate({ id: c.id, fijada })}
            onEliminar={eliminarConversacion}
            onReintentar={() => void historial.recargar()}
          />
        }
      >
        <ChatIaCuerpo
          composer={
            <ChatIaComposer
              inputRef={inputRef}
              valor={borrador}
              onCambiar={setBorrador}
              onEnviar={() => void enviar(borrador)}
              bloqueado={inputDeshabilitado && !guia.enviando}
              enviando={guia.enviando}
              maxLength={MAX_MENSAJE}
              placeholder={guia.bloqueado ? "Espera para volver a preguntar" : "Pregunta o pide un informe"}
              ariaLabel="Tu pregunta para la guía"
              pie={mostrarRestantes(guia.restantesHora) ? textoRestantes(guia.restantesHora) : undefined}
            />
          }
        >
          {abriendo && vacio ? (
            <EsqueletoHilo />
          ) : vacio ? (
            <ChatIaBienvenida
              descripcion="Pregúntame dónde está algo, cómo se hace un trámite o pídeme un informe."
              sugerencias={sugerencias}
              recientes={historial.conversaciones.slice(0, 3)}
              deshabilitado={inputDeshabilitado}
              onPreguntar={(s) => void enviar(s)}
              onRepetir={(c) => void repetirConversacion(c)}
            />
          ) : (
            <ChatIaMensajes
              mensajes={guia.mensajes}
              deshabilitado={inputDeshabilitado}
              escribiendo={guia.enviando}
              onRepetir={repetir}
              onEditar={editar}
              renderRespuesta={(m) => (
                <div className="space-y-1.5">
                  <div className="prose prose-sm max-w-full break-words text-[13.5px] leading-relaxed text-foreground dark:prose-invert prose-p:my-1.5 prose-ul:my-1.5 prose-ol:my-1.5 prose-li:my-0.5 prose-pre:overflow-x-auto prose-headings:mb-1 prose-headings:mt-3 prose-headings:text-[13.5px] prose-strong:text-foreground">
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdown}>
                      {m.texto}
                    </ReactMarkdown>
                  </div>
                  {m.enlaces.length > 0 && <Enlaces enlaces={m.enlaces} onIr={irA} />}
                </div>
              )}
            />
          )}

          {guia.error && guia.error.codigo !== "sin_permiso" && (
            <ChatIaAviso
              tono={guia.error.codigo === "limite_alcanzado" ? "aviso" : "error"}
              mensaje={guia.error.mensaje}
              detalle={
                guia.error.codigo === "limite_alcanzado" && guia.bloqueadoHasta !== null
                  ? `Disponible de nuevo en ${minutosRestantes(guia.bloqueadoHasta, guia.ahora)} min`
                  : undefined
              }
              onReintentar={guia.error.codigo === "error_modelo" ? () => void guia.reintentar(location.pathname) : undefined}
              reintentando={guia.enviando}
            />
          )}

          <div ref={finRef} />
        </ChatIaCuerpo>
      </ChatIaPanel>
    </>
  );
}

function Enlaces({ enlaces, onIr }: { enlaces: GuiaEnlace[]; onIr: (ruta: string) => void }) {
  return (
    <div className="flex max-w-full flex-wrap gap-1.5">
      {enlaces.map((e) => (
        <Button
          key={e.ruta}
          type="button"
          variant="primary-outline"
          size="sm"
          onClick={() => onIr(e.ruta)}
          className="h-auto min-h-8 max-w-full whitespace-normal px-2.5 py-1 text-left text-[12px]"
        >
          <span className="break-words">{e.etiqueta}</span>
          <ArrowRight />
        </Button>
      ))}
    </div>
  );
}
